// Pure maths for the planner: board grouping, timeline geometry, workload grading.

// Mirrors the backend's BOARD_COLUMNS, order included.
export const STATUSES = ['todo', 'in_progress', 'review', 'blocked', 'done'];

export const STATUS_LABEL = {
    todo: 'Offen',
    in_progress: 'In Arbeit',
    review: 'Review',
    blocked: 'Blockiert',
    done: 'Erledigt',
};

export const PRIORITIES = ['Low', 'Medium', 'High'];

export const PRIORITY_LABEL = { Low: 'Niedrig', Medium: 'Mittel', High: 'Hoch' };

export function groupByStatus(tasks) {
    const columns = Object.fromEntries(STATUSES.map(s => [s, []]));
    (tasks || []).forEach(task => {
        const key = STATUSES.includes(task?.status) ? task.status : 'todo';
        columns[key].push(task);
    });
    STATUSES.forEach(s => columns[s].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    return columns;
}

export function columnHours(tasks) {
    const totals = Object.fromEntries(STATUSES.map(s => [s, 0]));
    (tasks || []).forEach(task => {
        const key = STATUSES.includes(task?.status) ? task.status : 'todo';
        totals[key] += Number(task?.estimate_hours) || 0;
    });
    return totals;
}

// A plain `YYYY-MM-DD` is midnight UTC, whose local getters answer the day before west of
// UTC. Same reasoning as utils/dateConverter.js.
const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function toDate(value) {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const plain = String(value).match(PLAIN_DATE);
    const date = plain
        ? new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]))
        : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export const DAY_MS = 24 * 60 * 60 * 1000;

/** Counts both ends: a one-day task spans 1, not 0. */
export function daysBetween(a, b) {
    const from = toDate(a);
    const to = toDate(b);
    if (!from || !to) return 0;
    return Math.round((to - from) / DAY_MS) + 1;
}

export function addDays(value, days) {
    const date = toDate(value);
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export const toIso = (value) => {
    const date = toDate(value);
    if (!date) return null;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

/** Null when nothing is scheduled, so no axis is drawn around today. */
export function timelineRange(tasks, pad = 7) {
    const dates = [];
    (tasks || []).forEach(task => {
        const start = toDate(task?.planned_start);
        const end = toDate(task?.planned_end);
        if (start) dates.push(start);
        if (end) dates.push(end);
    });
    if (!dates.length) return null;
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    return { start: addDays(min, -pad), end: addDays(max, pad) };
}

/** Left and width as fractions of the window; null for an unscheduled task. */
export function barGeometry(task, range) {
    if (!range) return null;
    const start = toDate(task?.planned_start);
    const end = toDate(task?.planned_end) || start;
    if (!start) return null;
    const total = daysBetween(range.start, range.end);
    if (total <= 0) return null;
    const offset = daysBetween(range.start, start) - 1;
    const length = Math.max(1, daysBetween(start, end));
    return {
        left: offset / total,
        width: Math.min(length / total, 1 - offset / total),
        days: length,
    };
}

export function monthBands(range) {
    if (!range) return [];
    const total = daysBetween(range.start, range.end);
    const bands = [];
    let cursor = new Date(range.start.getFullYear(), range.start.getMonth(), 1);
    const last = toDate(range.end);
    while (cursor <= last) {
        const next = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
        const from = cursor < range.start ? range.start : cursor;
        const to = next > last ? last : addDays(next, -1);
        const offset = daysBetween(range.start, from) - 1;
        bands.push({
            key: `${cursor.getFullYear()}-${cursor.getMonth() + 1}`,
            label: cursor.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' }),
            left: offset / total,
            width: daysBetween(from, to) / total,
        });
        cursor = next;
    }
    return bands;
}

// `full` is deliberately not `free`: a week planned to the hour is the goal, not a warning.
export function loadState(planned, capacity) {
    const plan = Number(planned) || 0;
    const cap = Number(capacity) || 0;
    if (cap <= 0) return plan > 0 ? 'unavailable' : 'off';
    const ratio = plan / cap;
    if (ratio > 1.0001) return 'over';
    if (ratio >= 0.95) return 'full';
    if (ratio > 0) return 'partial';
    return 'free';
}

export const LOAD_LABEL = {
    over: 'überbucht',
    full: 'ausgelastet',
    partial: 'frei',
    free: 'nichts geplant',
    unavailable: 'abwesend, aber verplant',
    off: 'abwesend',
};

export function overbookedBy(planned, capacity) {
    const over = (Number(planned) || 0) - (Number(capacity) || 0);
    return over > 0 ? Math.round(over * 100) / 100 : 0;
}

export function weekLabel(week) {
    return `KW ${week?.iso_week ?? '?'}`;
}

/** Read off the response, never recomputed — a column and a row must agree on the week. */
export function weekColumns(users) {
    const seen = new Map();
    (users || []).forEach(user => {
        (user?.weeks || []).forEach(week => {
            const key = `${week.year}-${week.iso_week}`;
            if (!seen.has(key)) seen.set(key, week);
        });
    });
    return [...seen.values()].sort((a, b) => a.year - b.year || a.iso_week - b.iso_week);
}

export function projectTotals(tasks) {
    let planned = 0;
    let done = 0;
    let unscheduled = 0;
    (tasks || []).forEach(task => {
        const hours = Number(task?.estimate_hours) || 0;
        planned += hours;
        if (task?.status === 'done') done += hours;
        if (!task?.planned_start) unscheduled += 1;
    });
    return {
        planned: Math.round(planned * 100) / 100,
        done: Math.round(done * 100) / 100,
        open: Math.round((planned - done) * 100) / 100,
        unscheduled,
        count: (tasks || []).length,
    };
}

/** Tasks whose predecessor finishes after they start. */
export function brokenDependencies(tasks) {
    const byId = new Map((tasks || []).map(t => [String(t.id), t]));
    const broken = [];
    (tasks || []).forEach(task => {
        if (!task?.depends_on_id) return;
        const parent = byId.get(String(task.depends_on_id));
        if (!parent) return;
        const parentEnd = toDate(parent.planned_end) || toDate(parent.planned_start);
        const start = toDate(task.planned_start);
        if (parentEnd && start && start <= parentEnd) {
            broken.push({ task, parent });
        }
    });
    return broken;
}

/** Pointer offset as a share of the track width → whole days, at any zoom or width. */
export function daysFromFraction(fraction, range) {
    if (!range) return 0;
    return Math.round(fraction * daysBetween(range.start, range.end));
}

/** `mode` is 'move' (both ends), 'start' or 'end'. A resize keeps at least one day: the API
 *  rejects an end before the start. */
export function dragDates(task, mode, days) {
    const start = toDate(task?.planned_start);
    if (!start) return null;
    const end = toDate(task?.planned_end) || start;

    if (mode === 'move') {
        return { planned_start: toIso(addDays(start, days)), planned_end: toIso(addDays(end, days)) };
    }
    if (mode === 'start') {
        const moved = addDays(start, Math.min(days, daysBetween(start, end) - 1));
        return { planned_start: toIso(moved), planned_end: toIso(end) };
    }
    if (mode === 'end') {
        const moved = addDays(end, Math.max(days, -(daysBetween(start, end) - 1)));
        return { planned_start: toIso(start), planned_end: toIso(moved) };
    }
    return null;
}

export function isUnchanged(task, dates) {
    return !!dates
        && dates.planned_start === task?.planned_start
        && dates.planned_end === (task?.planned_end || task?.planned_start);
}
